import {
  AudiCodeClientError,
  AudiCodeServerError,
  ItemNotFoundError,
  LocaleNotSupportedError,
  MarketNotSupportedError,
  ModelTooOldError,
  UnauthorizedError,
  ValidationError,
  VinClientError,
  VinServerError,
  WltpDataNotAvailableYetError
} from '@/constants/exceptions';
import { domainName, GiveawayResponse, Stage } from '@/constants/interfaces';
import { brandContext } from '@/constants/locales';

export class HckClient {
  private endpoint: string;

  constructor(stage: Stage) {
    const domain =
      stage === Stage.PROD
        ? domainName
        : stage === Stage.TEST
        ? `${Stage.TEST}.${domainName}`
        : `${Stage.DEV}.${domainName}`;
    this.endpoint = `https://api.${domain}`;
  }

  public async getOrder(id: string): Promise<GiveawayResponse> {
    switch (brandContext) {
      case 'Cupra': {
        return this.doApiCall<void, GiveawayResponse>(
          'GET',
          `${this.endpoint}/v1/intern/brands/cupra/giveaways/${id}`
          // no idToken needed
        );
      }
      case 'Volkswagen': {
        return this.doApiCall<void, GiveawayResponse>(
          'GET',
          `${this.endpoint}/v1/intern/brands/vw/giveaways/${id}`
          // no idToken needed
        );
      }
      default: {
        return this.doApiCall<void, GiveawayResponse>(
          'GET',
          `${this.endpoint}/v1/intern/brands/audi/giveaways/${id}`
          // no idToken needed
        );
      }
    }
  }

  private async doApiCall<I, O>(method: string, url: string, idToken?: string, dto?: I): Promise<O> {
    try {
      const res = await this.callApi(method, url, idToken, dto);
      return this.handleSuccessResponse<O>(res);
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  private async callApi<I>(method: string, url: string, idToken?: string, dto?: I): Promise<Response> {
    const headers = this.createHeaders(idToken);
    return fetch(url, {
      method,
      headers,
      ...(dto ? { body: JSON.stringify(dto) } : {})
    });
  }

  private createHeaders(idToken?: string, contentType = 'application/json'): Record<string, string> {
    const headers: Record<string, string> = {};
    headers['Content-Type'] = contentType;
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    return headers;
  }

  private async handleSuccessResponse<T>(res: Response): Promise<T> {
    if (res.ok) {
      return res.json();
    }
    return this.handleFailureResponse(res);
  }

  private async handleFailureResponse<T>(res: Response): Promise<T> {
    const body = await res.json();
    switch (body.error) {
      case 'ValidationError':
        throw new ValidationError();
      case 'UnauthorizedError':
        throw new UnauthorizedError();
      case 'ItemNotFoundError':
        throw new ItemNotFoundError();
      case 'WltpDataNotAvailableYetError':
        throw new WltpDataNotAvailableYetError();
      case 'VinClientError':
        throw new VinClientError();
      case 'VinServerError':
        throw new VinServerError();
      case 'AudiCodeClientError':
        throw new AudiCodeClientError();
      case 'AudiCodeServerError':
        throw new AudiCodeServerError();
      case 'MarketNotSupportedError':
        throw new MarketNotSupportedError();
      case 'ModelTooOldError':
        throw new ModelTooOldError();
      case 'LocaleNotSupportedError':
        throw new LocaleNotSupportedError();
      default:
        throw new Error(res.statusText);
    }
  }
}
